import { css } from '@emotion/react';
import { Typography } from '@mui/material';
import { ColDef, IAggFuncParams, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { AgFormatterService } from '../../../../util/ag-formatter-service';
import { FMT } from '../../../../util/formatter-service';
import { getMoic, getOwnershipTotal, IEquityLot } from './equity-holdings-model';
import { getSecurityClass } from './security-class-model';
import { IConversionLink, IInPlaceRestructure } from './security-story-model';
import { getLifecycleChip, getMoicColor, isCarriedInvestment } from './security-history-model';
import { DistinctFundsAggFunc, FundCell } from './securityOverviewColDefs';

const nameCell = css`
  align-items: center;
  display: flex;
  gap: 0.375rem;
  min-width: 0;
`;

/**
 * The name anchors the row, as the design draws it — semibold against the chips' small caps.
 *
 * The floor matters: the name is the only flexible thing in the cell, so without one, a row carrying
 * two chips and the story button crushed it to nothing and read as tags with no security. The copy
 * wins; at real extremes the chips clip instead.
 */
const nameText = css`
  font-weight: 600;
  min-width: 6rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const lotCount = css`
  color: var(--mui-palette-text-secondary);
  flex-shrink: 0;
`;

/**
 * The design's `→ A-1` / `↳ SAFE` link chip, tinted by the restructure it belongs to. Typography
 * matches the lifecycle chip exactly — the tags read as one family, link or not.
 */
const linkChip = css`
  background: none;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  font: inherit;
  font-size: 0.4375rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 2;
  padding: 0 0.375rem;
  text-transform: uppercase;
`;

const storyChip = css`
  background: var(--mui-palette-action-hover);
  border: none;
  border-radius: 10px;
  color: var(--mui-palette-text-secondary);
  cursor: pointer;
  flex-shrink: 0;
  font: inherit;
  font-size: 0.625rem;
  font-weight: 600;
  padding: 0 0.375rem;
`;

/**
 * Lifecycle chip: the label in its own hue over a low-opacity wash of the same. Well below the row
 * text and a step lighter, so it reads as a tag beside the name rather than as more of the name.
 */
const chip = css`
  border-radius: 4px;
  flex-shrink: 0;
  font-size: 0.4375rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  line-height: 2;
  padding: 0 0.375rem;
  text-transform: uppercase;
`;

/** Name of the deduping ownership aggregation registered on the History grid. */
export const OwnershipAggFunc = 'equityOwnership';

/**
 * Ownership cannot be summed like the money columns. Every lot of a security carries that security's
 * whole-security percentage, so `sum` would report it once per fund — the double-count fixed in
 * `getOwnershipTotal`, reused here so a group row and the KPI strip agree.
 */
export function ownershipAggFunc(params: IAggFuncParams<IEquityLot, number | null>): number | null {
  const lots = (params.rowNode.allLeafChildren ?? [])
    .map((node) => node.data)
    .filter((data): data is IEquityLot => data != null);

  return getOwnershipTotal(lots);
}

export interface IHistoryNameCellParams {
  /** Every cross-lot conversion a row takes part in — up to two for the middle of a chain. */
  links: Map<string, IConversionLink[]>;
  /** Lots that restructured within themselves, so there is no counterpart row to link to. */
  inPlaceRestructures: Map<string, IInPlaceRestructure[]>;
  /** The counterpart's class alone — "A-1", "E", "Note" — which is all the chip has room for. */
  counterpartClass: (lotId: string) => string;
  /** One colour per capital thread: every chip and highlight on a thread shares it. */
  threadColorOf: (lotId: string) => string;
  onSelectStory: (lotId: string) => void;
  onHoverLot: (lotId: string | null) => void;
}

function HistoryNameCell(params: ICellRendererParams<IEquityLot> & IHistoryNameCellParams) {
  const {
    node,
    value,
    data,
    links,
    inPlaceRestructures,
    counterpartClass,
    threadColorOf,
    onSelectStory,
    onHoverLot,
  } = params;

  if (node.rowPinned) {
    return (
      <Typography variant='body2' fontWeight={600}>
        {'Total'}
      </Typography>
    );
  }

  const segment = value == null ? '' : String(value);
  // A leaf's path segment is the opaque lot id when a block was bought by several funds; see
  // `buildClassPaths`.
  const isOpaqueLotSegment = data != null && segment === data.id;
  const label = isOpaqueLotSegment ? data.securityName : segment;
  const leafCount = node.group ? (node.allLeafChildren?.length ?? 0) : 0;
  // Group rows describe a class or a block, which have no single lifecycle of their own.
  const lifecycleChip = node.group || data == null ? null : getLifecycleChip(data.lifecycle);

  const lotLinks = data == null ? [] : (links.get(data.id) ?? []);
  // An in-place restructure has no counterpart row, so it gets a plain chip rather than a link — but
  // only when no real cross-lot link exists, since a link says strictly more. Where the ledger names
  // what the lot converted from, the chip carries that class the way a link would — "↳ SAFE" — and
  // falls back to the bare verb when it does not.
  const isInPlace = data != null && inPlaceRestructures.has(data.id);
  const threadColor = data == null ? '' : threadColorOf(data.id);
  const inPlaceFrom = data == null ? null : classConvertedFrom(data);

  return (
    <div css={nameCell}>
      <span css={nameText}>{label}</span>
      {leafCount > 1 && <span css={lotCount}>{`(${leafCount} lots)`}</span>}
      {lifecycleChip !== null && (
        <span css={chip} style={{ color: lifecycleChip.color, backgroundColor: `${lifecycleChip.color}1F` }}>
          {lifecycleChip.label}
        </span>
      )}
      {isInPlace && lotLinks.length === 0 && (
        <span
          css={chip}
          style={{ color: threadColor, backgroundColor: `${threadColor}24` }}
          title='This security was restructured in place'
        >
          {inPlaceFrom === null ? 'Restructured' : `↳ ${inPlaceFrom}`}
        </span>
      )}
      {lotLinks.map((link) => {
        const color = threadColor;
        return (
          <button
            css={linkChip}
            key={link.pairId}
            onClick={(event) => {
              // The row click toggles expansion; the chip is about the conversion, not the tree.
              event.stopPropagation();
              onSelectStory(data?.id ?? '');
            }}
            onMouseEnter={() => onHoverLot(data?.id ?? null)}
            onMouseLeave={() => onHoverLot(null)}
            style={{ backgroundColor: `${color}24`, color }}
            type='button'
            title={link.direction === 'to' ? 'Converted into this security' : 'Converted from this security'}
          >
            {/* The class alone — "→ A-1", "↳ Note". The full name does not fit and is harder to scan. */}
            {`${link.direction === 'to' ? '→' : '↳'} ${counterpartClass(link.counterpartLotId)}`}
          </button>
        );
      })}
      {data != null && !node.group && (
        <button
          css={storyChip}
          onClick={(event) => {
            event.stopPropagation();
            onSelectStory(data.id);
          }}
          type='button'
        >
          {'story ›'}
        </button>
      )}
    </div>
  );
}

/**
 * The class a lot converted from, read off `initialHolding` — only where the ledger names one, it
 * parses to a real class, and that class differs from the lot's own. Anything less certain returns
 * null and the chip keeps the bare verb: a wrong lineage is worse than an unspecific one.
 */
function classConvertedFrom(lot: IEquityLot): string | null {
  if (lot.initialHolding == null || lot.initialHolding === '') {
    return null;
  }
  const from = getSecurityClass(lot.initialHolding);
  if (from.kind === 'other') {
    return null;
  }
  return from.shortLabel === getSecurityClass(lot.securityClass).shortLabel ? null : from.shortLabel;
}

/** A converted lot's capital was carried onward rather than returned, which the amount alone hides. */
function InvestedCell({ value, data }: ICellRendererParams<IEquityLot, number>) {
  const formatted = FMT.format('currencyShort', value);

  if (!isCarriedInvestment(data)) {
    return <>{formatted}</>;
  }
  return (
    <Typography variant='inherit' color='text.secondary' component='span'>
      {`${formatted} carried`}
    </Typography>
  );
}

function MoicCell({ value }: ICellRendererParams<IEquityLot, number | undefined>) {
  return (
    <span style={{ color: getMoicColor(value ?? undefined), fontWeight: 600 }}>
      {FMT.format('multiplier', value)}
    </span>
  );
}

/** Derived on leaves and recomputed from the aggregated money on group and pinned rows. */
function getMoicValue({ data, node }: ValueGetterParams<IEquityLot>): number | undefined {
  if (data != null) {
    return data.moic;
  }
  const agg = node?.aggData as Partial<Pick<IEquityLot, 'fmv' | 'proceeds' | 'invested'>> | undefined;
  if (agg == null) {
    return undefined;
  }
  return getMoic(agg.fmv ?? 0, agg.proceeds ?? 0, agg.invested ?? 0);
}

export function getSecurityHistoryAutoGroupColDef(
  nameCellParams: IHistoryNameCellParams
): ColDef<IEquityLot> {
  return {
    headerName: 'Security',
    // The design keeps the security in view while the remaining columns scroll horizontally.
    pinned: 'left',
    // Wider than Overview's: this column carries the lifecycle chip and the restructure links as
    // well as the name.
    minWidth: 380,
    flex: 2,
    cellRendererParams: {
      innerRenderer: HistoryNameCell,
      suppressCount: true,
      ...nameCellParams,
    },
  };
}

/** Narrowed once here rather than at every column: the service is generic over the config, not the row. */
const formatLotNumber = (id: 'integer' | 'currencyShort' | 'percent') =>
  AgFormatterService.get().getFormatterForId<IEquityLot, number>(id);

export function getSecurityHistoryColDefs(): ColDef<IEquityLot>[] {
  return [
    {
      headerName: 'Investment Date',
      field: 'investmentDate',
      minWidth: 140,
      valueFormatter: ({ value }) => FMT.format('date', value),
    },
    {
      headerName: 'Fund',
      colId: 'fundId',
      minWidth: 130,
      valueGetter: ({ data }) => data?.fundId ?? null,
      aggFunc: DistinctFundsAggFunc,
      cellRenderer: FundCell,
      filter: true,
      filterValueGetter: ({ data }) => (data?.fundId == null ? null : FMT.format('fund', data.fundId)),
    },
    {
      headerName: 'Shares',
      field: 'shares',
      type: 'numericColumn',
      minWidth: 110,
      aggFunc: 'sum',
      valueFormatter: formatLotNumber('integer'),
    },
    {
      headerName: 'Invested',
      field: 'invested',
      type: 'numericColumn',
      minWidth: 130,
      aggFunc: 'sum',
      cellRenderer: InvestedCell,
    },
    {
      headerName: 'Proceeds',
      field: 'proceeds',
      type: 'numericColumn',
      minWidth: 110,
      aggFunc: 'sum',
      valueFormatter: formatLotNumber('currencyShort'),
    },
    {
      headerName: 'Ownership %',
      field: 'ownershipPct',
      type: 'numericColumn',
      minWidth: 120,
      aggFunc: OwnershipAggFunc,
      valueFormatter: formatLotNumber('percent'),
    },
    {
      headerName: 'FMV',
      field: 'fmv',
      type: 'numericColumn',
      minWidth: 110,
      aggFunc: 'sum',
      valueFormatter: formatLotNumber('currencyShort'),
    },
    {
      headerName: 'MOIC',
      colId: 'moic',
      type: 'numericColumn',
      minWidth: 100,
      valueGetter: getMoicValue,
      cellRenderer: MoicCell,
    },
  ];
}
